import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip
} from '@mui/material';
import { Refresh as RefreshIcon, Edit as EditIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { usePermissions } from '../../context/AuthContext';

const STATUS_COLORS = {
  pending: 'warning',
  reviewed: 'info',
  approved: 'success',
  rejected: 'error',
  converted: 'primary'
};

const SignupRequests = () => {
  const { can } = usePermissions();
  const canEdit = can('tenants', 'edit');
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [status, setStatus] = useState('pending');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/catalog/signup-requests', {
        params: {
          page: 1,
          limit: 100,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          search: search || undefined
        }
      });
      setRows(res.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load signup requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const openReview = (row) => {
    setSelected(row);
    setAdminNotes(row.admin_notes || '');
    setStatus(row.status || 'pending');
  };

  const handleSave = async () => {
    if (!selected) return;
    try {
      await api.put(`/catalog/signup-requests/${selected.id}`, {
        status,
        admin_notes: adminNotes
      });
      toast.success('Signup request updated');
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Tenant Signup Requests</Typography>
          <Typography variant="body2" color="text.secondary">
            Requests submitted from the public Tenant Signup page.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2 }}>
        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="reviewed">Reviewed</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="converted">Converted</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={load}>Apply</Button>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Company</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Apps / Services</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center">No signup requests</TableCell></TableRow>
            ) : rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Typography fontWeight={600}>{row.company_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{row.company_address}</Typography>
                </TableCell>
                <TableCell>{row.contact_email}</TableCell>
                <TableCell>{row.plan?.name || row.selected_plan_code || '—'}</TableCell>
                <TableCell>
                  <Typography variant="caption">
                    Apps: {(row.selected_applications || []).length} · Services: {(row.selected_services || []).length}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip size="small" color={STATUS_COLORS[row.status] || 'default'} label={row.status} />
                </TableCell>
                <TableCell>{row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}</TableCell>
                <TableCell align="right">
                  {canEdit && (
                    <Tooltip title="Review">
                      <IconButton size="small" onClick={() => openReview(row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Review Signup — {selected?.company_name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2"><strong>Email:</strong> {selected?.contact_email}</Typography>
            <Typography variant="body2"><strong>Contact:</strong> {selected?.contact_name || '—'} {selected?.contact_phone ? `· ${selected.contact_phone}` : ''}</Typography>
            <Typography variant="body2"><strong>Address:</strong> {selected?.company_address || '—'}</Typography>
            <Typography variant="body2"><strong>Notes:</strong> {selected?.notes || '—'}</Typography>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="reviewed">Reviewed</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="converted">Converted</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Admin notes"
              multiline
              rows={3}
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SignupRequests;
